import {
    Booking as BookingModel,
    BookingStatus,
    useBook,
    useBookings,
    useBookingStatus,
    useBookingUpdate,
} from '@sugg-gestion/suggpro-graph-module'
import actions from 'core/store/actions'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import BookingDataView from 'components/views/booking/bookingDataView'
import { useApiErrors } from 'core/services/suggPro/useApiErrors'
import withAuthenticatedGraphApi from '../hoc/withAuthenticatedGraphApi'
import { BookingFormState } from 'components/forms/booking/bookingForm'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { WithGraphApiProps } from '../hoc/withGraphApi'

interface Props extends WithGraphApiProps {
    id: number
}

const BOOKINGS_HISTORY_MONTHS = 3

const BookingData: React.FC<Props> = ({ id }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    // AWSDateTime, mémorisé pour ne pas relancer la requête à chaque rendu
    const from = useMemo(
        () => moment().subtract(BOOKINGS_HISTORY_MONTHS, 'months').startOf('day').format(),
        [],
    )
    const { data, refetch, loading } = useBookings(id.toString(), from)
    const { updateBookingStatus } = useBookingStatus()
    const { book } = useBook()
    const { updateBooking } = useBookingUpdate()
    const { displayError } = useApiErrors()
    const { enqueueSnackbar } = useSnackbar()

    const [acceptBookingInProgress, setAcceptBookingInProgress] = useState<string>()
    const [rejectBookingInProgress, setRejectBookingInProgress] = useState<string>()

    let bookings: Array<BookingModel> = []
    if (data !== undefined) {
        bookings = data.bookings
    }

    const handleRefresh = () => {
        refetch()
    }

    const acceptBooking = (bookingId: string) => {
        setAcceptBookingInProgress(bookingId)
        return updateBookingStatus({
            variables: {
                id: bookingId,
                status: BookingStatus.Confirmed,
                sendEmail: true,
            },
        })
            .then(() => {
                enqueueSnackbar(t('views.booking.acceptBooking.success'), {
                    variant: 'success',
                })
            })
            .catch((e) => {
                displayError(e)
            })
            .finally(() => {
                setAcceptBookingInProgress(undefined)
            })
    }
    const rejectBooking = (bookingId: string) => {
        setRejectBookingInProgress(bookingId)
        return updateBookingStatus({
            variables: {
                id: bookingId,
                status: BookingStatus.Rejected,
                sendEmail: true,
            },
        })
            .then(() => {
                enqueueSnackbar(t('views.booking.rejectBooking.success'), {
                    variant: 'success',
                })
            })
            .catch((e) => {
                displayError(e)
            })
            .finally(() => {
                setRejectBookingInProgress(undefined)
            })
    }
    const handleAcceptBooking = (bookingId: string) => {
        dispatch(
            actions.alert({
                content: t('views.booking.verificationAccept'),
                buttons: [
                    {
                        color: 'primary',
                        children: t('common.no'),
                    },
                    {
                        color: 'primary',
                        children: t('common.yes'),
                        onClick: () => acceptBooking(bookingId),
                    },
                ],
            }),
        )
    }
    const handleRejectBooking = (bookingId: string) => {
        dispatch(
            actions.alert({
                content: t('views.booking.verificationReject'),
                buttons: [
                    {
                        color: 'primary',
                        children: t('common.no'),
                    },
                    {
                        color: 'primary',
                        children: t('common.yes'),
                        onClick: () => rejectBooking(bookingId),
                    },
                ],
            }),
        )
    }

    const [bookingEditInProgress, setBookingEditInProgress] = useState<boolean>(false)
    const handleEdit = (bookingId: string, values: BookingFormState) => {
        const bookingDate = values.date
        bookingDate.hour(values.time.hour()).minute(values.time.minute()).second(0)
        const phone = parsePhoneNumberFromString(values.phone, 'FR')
        if (phone === undefined || !phone.isValid()) {
            return Promise.resolve()
        }
        setBookingEditInProgress(true)
        return updateBooking({
            variables: {
                id: bookingId,
                input: {
                    establishmentId: id.toString(),
                    givenName: values.givenName,
                    familyName: values.familyName,
                    guestsNumber: values.guests,
                    bookingDate: bookingDate.format(),
                    email: values.email,
                    phone: phone.number as string,
                    comment: values.comment,
                },
            },
        })
            .then(() => {
                enqueueSnackbar(t('views.booking.update.success'), {
                    variant: 'success',
                })
            })
            .catch((e) => {
                displayError(e)
            })
            .finally(() => {
                setBookingEditInProgress(false)
            })
    }

    const [bookingAddInProgress, setBookingAddInProgress] = useState<boolean>(false)
    const handleAdd = (values: BookingFormState) => {
        const bookingDate = values.date
        bookingDate.hour(values.time.hour()).minute(values.time.minute()).second(0)
        const phone = parsePhoneNumberFromString(values.phone, 'FR')
        if (phone === undefined || !phone.isValid()) {
            return Promise.resolve()
        }
        setBookingAddInProgress(true)
        return book({
            variables: {
                account: id.toString(),
                input: {
                    establishmentId: id.toString(),
                    guestsNumber: values.guests,
                    bookingDate: bookingDate.format(),
                    givenName: values.givenName,
                    familyName: values.familyName,
                    email: values.email,
                    phone: phone.number as string,
                    comment: values.comment,
                },
                sendEstablishmentEmail: false,
                sendCustomerEmail: values.sendEmail,
            },
        })
            .then(() => {
                enqueueSnackbar(t('views.booking.saved.success'), {
                    variant: 'success',
                })
            })
            .catch((e) => {
                displayError(e)
            })
            .finally(() => {
                setBookingAddInProgress(false)
            })
    }

    return (
        <BookingDataView
            bookings={bookings}
            historyMonths={BOOKINGS_HISTORY_MONTHS}
            loading={loading}
            acceptBookingInProgress={acceptBookingInProgress}
            onAcceptBooking={handleAcceptBooking}
            rejectBookingInProgress={rejectBookingInProgress}
            onRejectBooking={handleRejectBooking}
            onRefresh={handleRefresh}
            onEditBooking={handleEdit}
            bookingEditInProgress={bookingEditInProgress}
            onAddBooking={handleAdd}
            bookingAddInProgress={bookingAddInProgress}
        />
    )
}

export default withAuthenticatedGraphApi(BookingData)
